/**
 * Position Action Button Component
 * Handles both cancel (for non-finalized) and withdraw (for finalized) positions
 *
 * Edge Cases Handled:
 * - Cancel before agent processes (finalized=false, vault_balance == deposited)
 * - Withdraw after finalization (finalized=true, credited > 0)
 * - Position already withdrawn (vault_balance=0)
 * - User not authenticated
 * - Transaction failures with proper error messages
 */

import React, { useState } from 'react';
import { Button, CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { toast } from 'react-toastify';
import { useWallet } from '@aptos-labs/wallet-adapter-react';
import { useQueryClient } from '@tanstack/react-query';
import { useUserContext } from 'context/userContext';
import { queryKeys, useUser } from 'api/hooks';
import {
	cancelPosition,
	formatUSDC,
	withdrawPosition,
} from 'core/vaultTransactions';
import { notifySlackError, notifyWithdraw } from 'utils/slack/slackNotifier';
import { ConsoleData } from 'utils/console';
import { finalizePosition, getPosition } from 'api/client';
import { VaultConfig } from 'core/constants';
import ProcessingOrderModal from 'components/ProcessingOrderModal';

const SecondaryButton = styled(Button)(({ theme }) => ({
	backgroundColor: theme.palette.primary.main,
	width: '100%',
	whiteSpace: 'nowrap',
	color: '#fff',
	padding: '12px 10px',
	fontFamily: 'AmigaForever',
	fontWeight: 600,
	fontSize: '14px',
	letterSpacing: '0.15em',
	textTransform: 'uppercase',
	boxShadow: 'none',
	clipPath:
		'polygon(15px 0, 100% 0, 100% calc(100% - 15px), calc(100% - 15px) 100%, 0 100%, 0 15px)',
	'&:hover': {
		backgroundColor: theme.palette.primary.dark,
		boxShadow: 'none',
	},
}));

interface WithdrawButtonProps {
	posId: number;
	vaultBalance: number;
	credited: number;
	finalized: boolean;
	onSuccess?: () => void;
	positionData: any;
	apiBaseUrl?: string;
	vaultConfig?: Partial<VaultConfig>;
}

export const WithdrawButton: React.FC<WithdrawButtonProps> = ({
	posId,
	vaultBalance,
	credited,
	finalized,
	onSuccess,
	positionData,
	apiBaseUrl,
	vaultConfig,
}) => {
	const [loading, setLoading] = useState(false);
	const { account: activeAccount, signAndSubmitTransaction } = useWallet();
	const { userId } = useUserContext();
	const queryClient = useQueryClient();
	const { userId: contextUserId, walletAddress } = useUserContext();
	const { data: userInfo } = useUser(
		contextUserId,
		!!contextUserId,
		apiBaseUrl,
	);
	const userAmount = parseInt(userInfo?.lastDeposit?.amountBase || '0');
	const positionAmount = parseInt(positionData?.result?.[1] || '0');
	const positionAddress = positionData?.result?.[0];
	const positionIdMatch = userInfo?.lastDeposit?.posId === positionData.id;
	const isAllocated = userAmount !== positionAmount;

	const actionType = finalized ? 'withdraw' : 'cancel';

	const handleAction = async () => {
		ConsoleData('On Withdraw click');
		if (!activeAccount || !userId || !signAndSubmitTransaction) {
			/* console.error('❌ Authentication check failed:', {
				hasActiveAccount: !!activeAccount,
				hasUserId: !!userId,
				hasSignFunction: !!signAndSubmitTransaction,
			});*/
			notifySlackError({
				title: '❌ Authentication check failed:',
				details: JSON.stringify({
					hasActiveAccount: !!activeAccount,
					hasUserId: !!userId,
					hasSignFunction: !!signAndSubmitTransaction,
				}),
				address: activeAccount?.address.toString() || '',
			});

			if (activeAccount && !userId) {
				ConsoleData('Active account', 'Not user id');
				notifySlackError({
					title: 'Session expired. Please log out and sign in again.',
					details:
						'Session expired. Please log out and sign in again.',
					address: activeAccount?.address.toString() || '',
				});
			} else {
				ConsoleData('withdraw Else');
				notifySlackError({
					title: 'Please connect your wallet and sign in first',
					details: 'Please connect your wallet and sign in first',
					address: activeAccount?.address.toString() || '',
				});
			}

			ConsoleData('withdraw Else');
			return;
		}

		if (
			userAmount > 0 &&
			positionIdMatch &&
			positionAddress !== walletAddress
		) {
			// console.error('❌ No funds in position', { posId, vaultBalance });
			toast.error('Position already withdrawn');
			return;
		}

		try {
			setLoading(true);

			// IMPORTANT: Handle "Close Agent" flow separately to avoid blocking transaction
			if (isAllocated && !finalized) {
				// This is the "Close Agent" flow - needs to finalize first
				// No transaction popup needed here, just API calls
				await finalizePosition(
					{
						userId,
						posId,
						sweepFeesFirst: true,
					},
					apiBaseUrl,
				);

				const pollForFinalized = async () => {
					const maxAttempts = 12;
					for (let attempt = 0; attempt < maxAttempts; attempt++) {
						try {
							const latestPosition = await getPosition(
								posId,
								apiBaseUrl,
							);
							const isFinalized = Array.isArray(
								latestPosition?.result,
							)
								? !!latestPosition.result[3]
								: false;

							if (isFinalized) return true;
						} catch (pollError) {
							/* console.error(
								'Polling position failed:',
								pollError,
							);*/
						}

						await new Promise((resolve) =>
							setTimeout(resolve, 5000),
						);
					}
					return false;
				};

				const finalizedNow = await pollForFinalized();
				if (finalizedNow) {
					await queryClient.invalidateQueries({
						queryKey: queryKeys.position(posId, apiBaseUrl),
					});
				}
				onSuccess?.();
				return;
			} else if (finalized) {
				// WITHDRAW FLOW - transaction required
				ConsoleData('finalized is true');
				if (credited === 0) {
					// console.error('❌ No funds credited', { posId, credited });
					toast.error('No funds available to withdraw');
					return;
				}

				// CRITICAL: Call transaction immediately, no await before this
				const txHash = await withdrawPosition(
					posId,
					signAndSubmitTransaction,
					vaultConfig,
				);

				ConsoleData('withdrawPosition', txHash);

				// Now safe to do async operations after transaction
				await queryClient.invalidateQueries({
					queryKey: queryKeys.position(posId, apiBaseUrl),
				});

				notifyWithdraw({
					title: `Withdrawn Successfully`,
					details: `Withdrawn ${formatUSDC(credited)} USDC`,
					address: activeAccount.address.toString(),
				});
			} else {
				// CANCEL FLOW (for non-finalized positions) - transaction required
				ConsoleData('finalized is false, so cancel flow is executing');

				// CRITICAL: Call transaction immediately, no await before this
				const txHash = await cancelPosition(
					posId,
					signAndSubmitTransaction,
					vaultConfig,
				);

				ConsoleData('cancelPosition', txHash);

				// Now safe to do async operations after transaction
				await queryClient.invalidateQueries({
					queryKey: queryKeys.position(posId, apiBaseUrl),
				});

				notifyWithdraw({
					title: `Withdrawn Successfully`,
					details: `Withdrawn ${formatUSDC(vaultBalance)} USDC`,
					address: activeAccount.address.toString(),
				});
			}

			// Wait for position query to refetch before calling onSuccess
			await queryClient.refetchQueries({
				queryKey: queryKeys.position(posId, apiBaseUrl),
			});

			onSuccess?.();
		} catch (error: any) {
			notifySlackError({
				title: `❌ ${actionType} failed:`,
				details: error,
				address: activeAccount.address.toString() || '',
			});
			// console.error(`❌ ${actionType} failed:`, error);
			/* console.error('Error details:', {
				message: error.message,
				code: error.code,
				data: error.data,
				stack: error.stack,
			});*/

			let errorMessage = 'Withdraw failed';

			if (error.message) {
				errorMessage = error.message;
			}

			ConsoleData('error', errorMessage);

			notifyWithdraw(
				{
					title: `Withdrawn ${formatUSDC(vaultBalance)} Failed`,
					details: errorMessage,
					address: activeAccount.address.toString(),
				},
				'error',
			);
		} finally {
			setLoading(false);
		}
	};

	// If already withdrawn, don't show button
	if (vaultBalance === 0) {
		return null;
	}

	// Show appropriate button based on finalization state
	if (finalized) {
		// Finalized = can withdraw
		return (
			<SecondaryButton
				onClick={handleAction}
				disabled={loading}
			>
				{loading ? (
					<CircularProgress size={16} color="inherit" />
				) : (
					<Typography
						variant="caption"
						fontWeight="bold"
						color="inherit"
					>
						Withdraw
					</Typography>
				)}
			</SecondaryButton>
		);
	} else {
		return (
			<>
				<ProcessingOrderModal open={loading && isAllocated} />
				<SecondaryButton
					onClick={handleAction}
					disabled={loading}
				>
					{loading ? (
						<CircularProgress size={16} color="inherit" />
					) : (
						<Typography
							variant="caption"
							fontWeight="bold"
							color="inherit"
						>
							{isAllocated ? 'Close Agent' : 'Withdraw'}
						</Typography>
					)}
				</SecondaryButton>
			</>
		);
	}
};
